import React, { useEffect, useState } from 'react';
import { Check, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { isOffline, isDemoMode } from '@/lib/supabase/index';
import { useContactForm } from '@/contexts/ContactFormContext';
import { getSessionId, getUtmParams, getDeviceInfo } from '@/lib/analytics';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { sendNotification } from '@/lib/services/notificationService';
import {
  checkRateLimit,
  getTimeUntilRateLimitReset,
  RATE_LIMIT_KEYS,
} from '@/lib/utils/rateLimit';
import {
  trackContactFormSuccess,
  trackFormStart,
  trackGenerateLead,
} from '@/lib/utils/dataLayer';
import { getPendingLeadAttribution } from '@/lib/utils/leadAttribution';
import { useFormShield } from '@/hooks/useFormShield';
import TurnstileWidget from '@/components/common/TurnstileWidget';
import SinglePageContactForm from './SinglePageContactForm';

interface ContactFormProps {
  isDialog?: boolean;
  showHeader?: boolean;
  /**
   * Render the panel on its own, without the page-level `<section>` padding,
   * the nested `.container` or the `max-w-3xl` cap. Set this when the form is
   * dropped inside a layout that already provides those, so it doesn't fight
   * the host's gutters — a landing-page quote block, for example.
   */
  embedded?: boolean;
  /** Dedicated /contact layout; homepage and modal retain their steps. */
  singlePage?: boolean;
}

const ContactForm = ({
  isDialog = false,
  showHeader = true,
  embedded = false,
  singlePage = false,
}: ContactFormProps) => {
  const isSinglePage = singlePage && !isDialog;
  const { toast } = useToast();
  const { closeContactForm, selectedServices } = useContactForm();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    services: [] as string[],
    primaryGoal: '',
    smsConsent: false,
  });

  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    smsConsent: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const shield = useFormShield();
  const [submissionState, setSubmissionState] = useState<'idle' | 'success'>(
    'idle'
  );
  const [hasTrackedFormStart, setHasTrackedFormStart] = useState(false);
  const [step, setStep] = useState(1);
  const totalSteps = 3;

  const formTrackingContext = isDialog
    ? 'contact_modal'
    : isSinglePage
      ? 'contact_page_form'
      : 'homepage_contact_form';

  const handleFormStart = () => {
    if (hasTrackedFormStart) return;

    trackFormStart('contact_form', {
      form_context: formTrackingContext,
      services: formData.services,
    });
    setHasTrackedFormStart(true);
  };

  // Initialize with pre-selected services from context
  useEffect(() => {
    if (selectedServices.length > 0) {
      setFormData(prev => ({
        ...prev,
        services: selectedServices,
      }));
    }
  }, [selectedServices]);

  // Handle Ctrl+Enter key combination for form submission
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        const activeForm = document.querySelector('form');
        if (activeForm) {
          e.preventDefault();
          // Create and dispatch submit event on the form
          const submitEvent = new Event('submit', {
            bubbles: true,
            cancelable: true,
          });
          activeForm.dispatchEvent(submitEvent);
        }
      }
    };

    // Only add listener in dialog mode to prevent conflicts
    if (isDialog) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isDialog]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;

    // Handle checkbox specifically
    if (type === 'checkbox' && name === 'smsConsent') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
      // Clear checkbox error if checked
      if (checked && formErrors.smsConsent) {
        setFormErrors(prev => ({ ...prev, smsConsent: '' }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear errors when user types
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleServiceSelect = (service: string) => {
    setFormData(prev => {
      // If service already exists in the array, remove it; otherwise, add it
      const serviceExists = prev.services.includes(service);
      if (serviceExists) {
        return { ...prev, services: prev.services.filter(s => s !== service) };
      } else {
        return { ...prev, services: [...prev.services, service] };
      }
    });
  };

  const handlePrimaryGoalSelect = (primaryGoal: string) => {
    setFormData(prev => ({
      ...prev,
      primaryGoal: prev.primaryGoal === primaryGoal ? '' : primaryGoal,
    }));
  };

  const validateStep = (currentStep: number): boolean => {
    const errors = { ...formErrors };
    let valid = true;

    if (currentStep === 1) {
      if (!formData.name.trim()) {
        errors.name = 'Name is required';
        valid = false;
      } else {
        errors.name = '';
      }
      if (!formData.email.trim()) {
        errors.email = 'Email is required';
        valid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email address';
        valid = false;
      } else {
        errors.email = '';
      }
    }

    if (currentStep === 2) {
      const phoneCleaned = formData.phone.replace(/\D/g, '');
      if (!formData.phone.trim()) {
        errors.phone = 'Phone number is required';
        valid = false;
      } else if (phoneCleaned.length < 10) {
        errors.phone = 'Please enter a valid 10-digit phone number';
        valid = false;
      } else {
        errors.phone = '';
      }
    }

    if (currentStep === 3) {
      errors.message = '';
    }

    setFormErrors(errors);
    return valid;
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const validateForm = (): {
    valid: boolean;
    errors: typeof formErrors;
  } => {
    let valid = true;
    const errors = {
      name: '',
      email: '',
      phone: '',
      message: '',
      smsConsent: '',
    };

    // Validate name
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
      valid = false;
    }

    // Validate email
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      valid = false;
    }

    // Validate phone (now required)
    const phoneCleaned = formData.phone.replace(/\D/g, '');
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
      valid = false;
    } else if (phoneCleaned.length < 10) {
      // Basic length check
      errors.phone = 'Please enter a valid 10-digit phone number';
      valid = false;
    }

    // Message is optional; service/goal buttons carry the quick-intent signal.
    errors.message = '';

    setFormErrors(errors);
    return { valid, errors };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { valid, errors } = validateForm();
    if (!valid) {
      // Find the first field with an error and focus it
      const firstErrorField = Object.keys(errors).find(
        key => errors[key as keyof typeof errors]
      );
      if (firstErrorField) {
        const element = document.getElementById(
          isSinglePage ? `contact-page-${firstErrorField}` : firstErrorField
        );
        element?.focus();
      }
      return;
    }

    const sessionId = getSessionId();

    // Client-side rate limit: 5 submissions/hour for contact form
    const okClient = checkRateLimit(
      RATE_LIMIT_KEYS.CONTACT_FORM,
      5,
      60 * 60 * 1000
    );
    if (!okClient) {
      const waitMs = getTimeUntilRateLimitReset(
        RATE_LIMIT_KEYS.CONTACT_FORM,
        60 * 60 * 1000
      );
      const waitSec = Math.ceil(waitMs / 1000);
      toast({
        title: 'Rate Limited',
        description: `Please wait ${waitSec}s before submitting again.`,
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if offline
      if (isOffline) {
        toast({
          title: 'Offline',
          description:
            'You are currently offline. Your message will be sent when you reconnect.',
        });
        setIsSubmitting(false);
        return;
      }

      // Handle demo mode with simulated success
      if (isDemoMode) {
        // Simulate a delay like a real request
        await new Promise(resolve => setTimeout(resolve, 1200));

        toast({
          title: 'Demo Mode',
          description: 'Form submission is disabled in demo mode.',
        });

        // Reset the form and redirect to thank-you page
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
          services: [],
          primaryGoal: '',
          smsConsent: false,
        });

        // Close dialog if in dialog mode
        if (isDialog) {
          closeContactForm();
        }

        // Redirect to thank-you page
        navigate('/thank-you');

        setIsSubmitting(false);
        return;
      }

      // Prepare submission data
      const leadAttribution = getPendingLeadAttribution({
        cta_location: formTrackingContext,
        form_variant: isDialog
          ? 'modal_multi_step_contact_form'
          : isSinglePage
            ? 'contact_page_single_step_form'
            : 'homepage_multi_step_contact_form',
      });
      const submissionData = {
        form_type: 'contact_form',
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim() || null,
        phone: formData.phone.trim(),
        message: formData.message.trim() || null,
        services: formData.services,
        primary_goal: formData.primaryGoal || null,
        session_id: sessionId,
        utm_params: getUtmParams(),
        device_info: getDeviceInfo(),
        sms_consent: formData.smsConsent,
        landing_page: leadAttribution.landing_page,
        page_path: leadAttribution.page_path,
        cta_name: leadAttribution.cta_name,
        cta_location: leadAttribution.cta_location,
        offer_type: leadAttribution.offer_type,
        service_context: leadAttribution.service_context,
        form_variant: leadAttribution.form_variant,
        referrer: leadAttribution.referrer,
      };

      // Submit through the server, not straight into Supabase with the anon
      // key. The endpoint verifies the Turnstile token, the honeypot and the
      // time-on-form before it writes anything — none of which can be enforced
      // on a client-side insert, which is how the junk CRM contacts got in.
      const submitRes = await fetch('/api/leads/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...shield.payload(), ...submissionData }),
      });
      const submitBody = (await submitRes.json().catch(() => ({}))) as {
        success?: boolean;
        submission_id?: string;
        error?: string;
      };

      if (!submitRes.ok || !submitBody.success) {
        if (isSinglePage) shield.resetToken();
        console.error('Error submitting form:', submitBody.error);
        toast({
          title: 'Error',
          description:
            submitBody.error ??
            'Unable to submit your message. Please try again.',
          variant: 'destructive',
        });
        setIsSubmitting(false);
        return;
      }

      const insertedSubmission = submitBody.submission_id
        ? { id: submitBody.submission_id }
        : null;

      // Track only after the Supabase insert succeeds, so analytics count real leads.
      const successTrackingPayload = {
        form_context: formTrackingContext,
        services: formData.services,
        primary_goal: formData.primaryGoal || undefined,
        submission_id: insertedSubmission?.id,
        cta_name: leadAttribution.cta_name,
        cta_location: leadAttribution.cta_location,
        offer_type: leadAttribution.offer_type,
        service_context: leadAttribution.service_context,
        form_variant: leadAttribution.form_variant,
        landing_page: leadAttribution.landing_page,
      };

      trackContactFormSuccess('contact_form', successTrackingPayload);
      trackGenerateLead('contact_form', {
        form_name: 'contact_form',
        ...successTrackingPayload,
      });

      // Fire-and-forget: sync the saved lead to HubSpot from the server.
      if (insertedSubmission?.id) {
        fetch('/api/crm/sync-submission', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ submission_id: insertedSubmission.id }),
        }).catch(error => console.warn('HubSpot sync trigger failed:', error));
      }

      // Fire-and-forget: send Resend email notifications
      fetch('/api/contact/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          company: formData.company.trim() || undefined,
          message: formData.message.trim(),
          services: formData.services,
        }),
      }).catch(console.error);

      // Send email notification via Edge Function
      sendNotification({
        event_type: 'form_submission',
        priority: 'high',
        title: 'New Contact Form Submission',
        summary: `Name: ${formData.name} | Email: ${formData.email} | Phone: ${formData.phone}`,
        data: submissionData,
      });

      // Success! Reset form and redirect to thank-you page
      toast({
        title: 'Success!',
        description: "Thanks for reaching out! We'll be in touch soon!",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        services: [],
        primaryGoal: '',
        smsConsent: false,
      });
      setFormErrors({
        name: '',
        email: '',
        phone: '',
        message: '',
        smsConsent: '',
      });

      // Close dialog if in dialog mode
      if (isDialog) {
        closeContactForm();
      }

      // Redirect to thank-you page
      navigate('/thank-you');
    } catch (error: any) {
      console.error('Form submission error:', error);

      // Provide a more user-friendly error message
      let errorMessage =
        error.message || 'Failed to send your message. Please try again.';

      // Check for common error types and provide friendly messages
      if (
        errorMessage.includes('fetch') ||
        errorMessage.includes('network') ||
        errorMessage.includes('ENOTFOUND')
      ) {
        errorMessage =
          "We're having trouble connecting to our servers. Please check your internet connection and try again.";
      } else if (error.code === '42P01') {
        errorMessage = 'Database setup incomplete. Please contact support.';
      } else if (error.code === '23505') {
        errorMessage = "We've already received your message. Thank you!";
        // Redirect to thank-you page for duplicate submissions too
        setTimeout(() => navigate('/thank-you'), 1000);
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSinglePage) {
    return (
      <SinglePageContactForm
        data={formData}
        errors={formErrors}
        shield={shield}
        submitting={isSubmitting}
        onSubmit={handleSubmit}
        onStart={handleFormStart}
        onChange={handleChange}
        onService={handleServiceSelect}
        onGoal={handlePrimaryGoalSelect}
        onConsent={checked =>
          setFormData(prev => ({ ...prev, smsConsent: checked }))
        }
      />
    );
  }

  // If used in a dialog, we don't need the section wrapper and some styling adjustments
  if (isDialog) {
    return (
      <div className="px-2 py-4 relative">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold">
            Tell Us How We Can Elevate{' '}
            <span className="text-elev8-blue">Your Business</span>
          </h2>
          <p className="text-white/60 text-sm mt-2">
            Press Ctrl+Enter to submit the form
          </p>
        </div>

        <div className="glass-panel rounded-xl p-6 relative overflow-hidden backdrop-blur-xl">
          {/* Add enhanced subtle glow effect */}
          <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-elev8-blue/10 filter blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full bg-elev8-blue/5 filter blur-3xl"></div>
          <div className="absolute top-1/2 left-1/3 w-30 h-30 rounded-full bg-elev8-blue/5 filter blur-2xl"></div>

          {submissionState === 'success' ? (
            <div className="text-center py-8 relative z-10">
              <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
              <p className="text-white/70 mb-8">
                {formData.smsConsent
                  ? "Thanks for reaching out! You're now subscribed to SMS updates from ELEV8 Growth Solutions. We'll be in touch soon. Reply STOP at any time to unsubscribe."
                  : "Thanks for reaching out! We'll be in touch soon."}
              </p>
              <Button onClick={closeContactForm} className="btn-primary">
                Close
              </Button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              onFocusCapture={handleFormStart}
              className="space-y-6 relative z-10"
            >
              <StepIndicator currentStep={step} totalSteps={totalSteps} />

              {/* Spam shield: hidden honeypot + invisible Turnstile, both
                  verified server-side in /api/leads/submit. */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
                {...shield.honeypot}
              />
              <TurnstileWidget
                onVerify={shield.onTurnstileVerify}
                onUnavailable={shield.onTurnstileUnavailable}
                className="flex justify-center"
              />
              {shield.turnstileState === 'unavailable' && (
                <p className="text-amber-300/80 text-xs text-center">
                  Our spam check could not load — an ad blocker is the usual
                  cause. You can still submit; if it is rejected, disable the
                  blocker for this page or email us at
                  max@elev8growth.solutions.
                </p>
              )}

              {/* Step 1: Identity */}
              {step === 1 && (
                <div className="space-y-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-white/90 mb-2 text-sm font-medium"
                    >
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={cn(
                        'input-field',
                        formErrors.name && 'border-red-500 focus:border-red-500'
                      )}
                      placeholder="Your Name"
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-white/90 mb-2 text-sm font-medium"
                    >
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={cn(
                        'input-field',
                        formErrors.email &&
                          'border-red-500 focus:border-red-500'
                      )}
                      placeholder="your@email.com"
                    />
                    {formErrors.email && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.email}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2: Business Context */}
              {step === 2 && (
                <div className="space-y-6">
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-white/90 mb-2 text-sm font-medium"
                    >
                      Company
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Your Company Name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-white/90 mb-2 text-sm font-medium"
                    >
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={cn(
                        'input-field',
                        formErrors.phone &&
                          'border-red-500 focus:border-red-500'
                      )}
                      placeholder="(XXX) XXX-XXXX"
                    />
                    {formErrors.phone && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-white/90 mb-1 text-sm font-medium">
                      Service Interested In
                    </label>
                    <p className="text-white/60 text-xs mb-2">
                      Select all that apply
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <ServiceButton
                        selected={formData.services.includes('AGENTS')}
                        onClick={() => handleServiceSelect('AGENTS')}
                      >
                        AGENTS
                      </ServiceButton>
                      <ServiceButton
                        selected={formData.services.includes('ADS')}
                        onClick={() => handleServiceSelect('ADS')}
                      >
                        ADS
                      </ServiceButton>
                      <ServiceButton
                        selected={formData.services.includes('AUTOMATION')}
                        onClick={() => handleServiceSelect('AUTOMATION')}
                      >
                        AUTOMATION
                      </ServiceButton>
                      <ServiceButton
                        selected={formData.services.includes('WEBSITE')}
                        onClick={() => handleServiceSelect('WEBSITE')}
                      >
                        WEBSITE
                      </ServiceButton>
                    </div>
                  </div>
                  <div>
                    <label className="block text-white/90 mb-1 text-sm font-medium">
                      What do you want help with first?
                    </label>
                    <p className="text-white/60 text-xs mb-2">
                      Optional, but it helps us route your request faster.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <ServiceButton
                        selected={formData.primaryGoal === 'more_leads'}
                        onClick={() => handlePrimaryGoalSelect('more_leads')}
                      >
                        More qualified leads
                      </ServiceButton>
                      <ServiceButton
                        selected={formData.primaryGoal === 'missed_calls'}
                        onClick={() => handlePrimaryGoalSelect('missed_calls')}
                      >
                        Miss fewer calls
                      </ServiceButton>
                      <ServiceButton
                        selected={formData.primaryGoal === 'better_website'}
                        onClick={() =>
                          handlePrimaryGoalSelect('better_website')
                        }
                      >
                        Better website
                      </ServiceButton>
                      <ServiceButton
                        selected={formData.primaryGoal === 'automate_follow_up'}
                        onClick={() =>
                          handlePrimaryGoalSelect('automate_follow_up')
                        }
                      >
                        Automate follow-up
                      </ServiceButton>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Message & Consent */}
              {step === 3 && (
                <div className="space-y-6">
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-white/90 mb-2 text-sm font-medium"
                    >
                      Anything else we should know?
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className={cn(
                        'input-field resize-none',
                        formErrors.message &&
                          'border-red-500 focus:border-red-500'
                      )}
                      placeholder="Optional: add context, timing, or what you already tried..."
                    ></textarea>
                    {formErrors.message && (
                      <p className="text-red-500 text-xs mt-1">
                        {formErrors.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="smsConsent"
                        name="smsConsent"
                        checked={formData.smsConsent}
                        onCheckedChange={checked => {
                          setFormData(prev => ({
                            ...prev,
                            smsConsent: Boolean(checked),
                          }));
                        }}
                      />
                      <Label
                        htmlFor="smsConsent"
                        className="text-sm font-medium leading-none text-white/90 cursor-pointer"
                      >
                        I agree to receive SMS messages from ELEV8 Growth
                        Solutions
                      </Label>
                    </div>
                    <p className="text-xs text-white/60 pl-6">
                      By submitting this form, you agree to receive SMS messages
                      from ELEV8 Growth Solutions. Message and data rates may
                      apply. Reply STOP to unsubscribe.
                    </p>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-4">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="btn-secondary text-sm px-4 py-2"
                  >
                    Back
                  </button>
                ) : (
                  <div />
                )}
                {step < totalSteps ? (
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="btn-primary text-sm px-6 py-2"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting || !shield.ready}
                    className="btn-primary"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      'Submit'
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Original section for home page
  return (
    <section
      id={embedded ? undefined : 'contact-form'}
      className={cn('relative', !embedded && 'py-20')}
    >
      <div className={cn(embedded ? 'w-full' : 'container mx-auto px-4')}>
        {showHeader && (
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-2">
              Get in Touch
            </h2>
            <p className="text-lg text-white/70 max-w-xl mx-auto">
              Ready to elevate your business? Tell us about your needs and we'll
              show you how we can help.
            </p>
          </div>
        )}

        <div className={cn('mx-auto', embedded ? 'w-full' : 'max-w-3xl')}>
          <div
            className={cn(
              'glass-panel rounded-2xl p-6 md:p-8 shadow-xl relative overflow-hidden backdrop-blur-xl',
              isDialog ? 'md:max-w-md mx-auto' : 'md:p-10',
              embedded && 'md:p-8'
            )}
          >
            {/* Add enhanced subtle glow effect */}
            <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-elev8-blue/10 filter blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full bg-elev8-blue/5 filter blur-3xl"></div>
            <div className="absolute top-1/2 left-1/3 w-30 h-30 rounded-full bg-elev8-blue/5 filter blur-2xl"></div>

            {submissionState === 'success' ? (
              <div className="text-center py-8 relative z-10">
                <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Thank You!</h3>
                <p className="text-white/70 mb-8">
                  {formData.smsConsent
                    ? "Thanks for reaching out! You're now subscribed to SMS updates from ELEV8 Growth Solutions. We'll be in touch soon. Reply STOP at any time to unsubscribe."
                    : "Thanks for reaching out! We'll be in touch soon."}
                </p>
                {isDialog ? (
                  <Button onClick={closeContactForm} className="btn-primary">
                    Close
                  </Button>
                ) : (
                  <Button
                    onClick={() => setSubmissionState('idle')}
                    className="btn-primary"
                  >
                    Send Another Message
                  </Button>
                )}
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                onFocusCapture={handleFormStart}
                className="space-y-6 relative z-10"
              >
                <StepIndicator currentStep={step} totalSteps={totalSteps} />

                {/* Spam shield: hidden honeypot + invisible Turnstile, both
                    verified server-side in /api/leads/submit. */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                  {...shield.honeypot}
                />
                <TurnstileWidget
                  onVerify={shield.onTurnstileVerify}
                  onUnavailable={shield.onTurnstileUnavailable}
                  className="flex justify-center"
                />
                {shield.turnstileState === 'unavailable' && (
                  <p className="text-amber-300/80 text-xs text-center">
                    Our spam check could not load — an ad blocker is the usual
                    cause. You can still submit; if it is rejected, disable the
                    blocker for this page or email us at
                    max@elev8growth.solutions.
                  </p>
                )}

                {/* Step 1: Identity */}
                {step === 1 && (
                  <div className="space-y-6 animate-fade-in">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="section-name"
                          className="block text-white/90 mb-2 text-sm font-medium"
                        >
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="section-name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={cn(
                            'input-field',
                            formErrors.name &&
                              'border-red-500 focus:border-red-500'
                          )}
                          placeholder="Your Name"
                        />
                        {formErrors.name && (
                          <p className="text-red-500 text-xs mt-1">
                            {formErrors.name}
                          </p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="section-email"
                          className="block text-white/90 mb-2 text-sm font-medium"
                        >
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="section-email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={cn(
                            'input-field',
                            formErrors.email &&
                              'border-red-500 focus:border-red-500'
                          )}
                          placeholder="your@email.com"
                        />
                        {formErrors.email && (
                          <p className="text-red-500 text-xs mt-1">
                            {formErrors.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Business Context */}
                {step === 2 && (
                  <div className="space-y-6 animate-fade-in">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          htmlFor="section-company"
                          className="block text-white/90 mb-2 text-sm font-medium"
                        >
                          Company
                        </label>
                        <input
                          type="text"
                          id="section-company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="input-field"
                          placeholder="Your Company Name"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="section-phone"
                          className="block text-white/90 mb-2 text-sm font-medium"
                        >
                          Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="section-phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={cn(
                            'input-field',
                            formErrors.phone &&
                              'border-red-500 focus:border-red-500'
                          )}
                          placeholder="(XXX) XXX-XXXX"
                        />
                        {formErrors.phone && (
                          <p className="text-red-500 text-xs mt-1">
                            {formErrors.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-white/90 mb-1 text-sm font-medium">
                        Service Interested In
                      </label>
                      <p className="text-white/60 text-xs mb-2">
                        Select all that apply
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <ServiceButton
                          selected={formData.services.includes('AGENTS')}
                          onClick={() => handleServiceSelect('AGENTS')}
                        >
                          AGENTS
                        </ServiceButton>
                        <ServiceButton
                          selected={formData.services.includes('ADS')}
                          onClick={() => handleServiceSelect('ADS')}
                        >
                          ADS
                        </ServiceButton>
                        <ServiceButton
                          selected={formData.services.includes('AUTOMATION')}
                          onClick={() => handleServiceSelect('AUTOMATION')}
                        >
                          AUTOMATION
                        </ServiceButton>
                        <ServiceButton
                          selected={formData.services.includes('WEBSITE')}
                          onClick={() => handleServiceSelect('WEBSITE')}
                        >
                          WEBSITE
                        </ServiceButton>
                      </div>
                    </div>
                    <div>
                      <label className="block text-white/90 mb-1 text-sm font-medium">
                        What do you want help with first?
                      </label>
                      <p className="text-white/60 text-xs mb-2">
                        Optional, but it helps us route your request faster.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <ServiceButton
                          selected={formData.primaryGoal === 'more_leads'}
                          onClick={() => handlePrimaryGoalSelect('more_leads')}
                        >
                          More qualified leads
                        </ServiceButton>
                        <ServiceButton
                          selected={formData.primaryGoal === 'missed_calls'}
                          onClick={() =>
                            handlePrimaryGoalSelect('missed_calls')
                          }
                        >
                          Miss fewer calls
                        </ServiceButton>
                        <ServiceButton
                          selected={formData.primaryGoal === 'better_website'}
                          onClick={() =>
                            handlePrimaryGoalSelect('better_website')
                          }
                        >
                          Better website
                        </ServiceButton>
                        <ServiceButton
                          selected={
                            formData.primaryGoal === 'automate_follow_up'
                          }
                          onClick={() =>
                            handlePrimaryGoalSelect('automate_follow_up')
                          }
                        >
                          Automate follow-up
                        </ServiceButton>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Message & Consent */}
                {step === 3 && (
                  <div className="space-y-6 animate-fade-in">
                    <div>
                      <label
                        htmlFor="section-message"
                        className="block text-white/90 mb-2 text-sm font-medium"
                      >
                        Anything else we should know?
                      </label>
                      <textarea
                        id="section-message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className={cn(
                          'input-field resize-none',
                          formErrors.message &&
                            'border-red-500 focus:border-red-500'
                        )}
                        placeholder="Optional: add context, timing, or what you already tried..."
                      ></textarea>
                      {formErrors.message && (
                        <p className="text-red-500 text-xs mt-1">
                          {formErrors.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="section-smsConsent"
                          name="smsConsent"
                          checked={formData.smsConsent}
                          onCheckedChange={checked => {
                            setFormData(prev => ({
                              ...prev,
                              smsConsent: Boolean(checked),
                            }));
                            if (checked) {
                              setFormErrors(prev => ({
                                ...prev,
                                smsConsent: '',
                              }));
                            }
                          }}
                        />
                        <Label
                          htmlFor="section-smsConsent"
                          className="text-sm font-medium leading-none text-white/90 cursor-pointer"
                        >
                          I agree to receive SMS messages from ELEV8 Growth
                          Solutions
                        </Label>
                      </div>
                      <p className="text-xs text-white/60 pl-6">
                        By submitting this form, you agree to receive SMS
                        messages from ELEV8 Growth Solutions. Message and data
                        rates may apply. Reply STOP to unsubscribe.
                      </p>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between pt-4">
                  {step > 1 ? (
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="btn-secondary text-sm px-4 py-2"
                    >
                      Back
                    </button>
                  ) : (
                    <div />
                  )}
                  {step < totalSteps ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="btn-primary text-sm px-6 py-2"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting || !shield.ready}
                      className="btn-primary"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const StepIndicator = ({
  currentStep,
  totalSteps,
}: {
  currentStep: number;
  totalSteps: number;
}) => (
  <div className="flex items-center justify-center mb-8">
    {Array.from({ length: totalSteps }, (_, i) => {
      const stepNum = i + 1;
      const isActive = stepNum === currentStep;
      const isCompleted = stepNum < currentStep;
      return (
        <React.Fragment key={stepNum}>
          <div
            className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300',
              isActive
                ? 'bg-elev8-blue text-white scale-110'
                : isCompleted
                  ? 'bg-elev8-blue/60 text-white'
                  : 'bg-white/10 text-white/40'
            )}
          >
            {isCompleted ? <Check size={14} /> : stepNum}
          </div>
          {stepNum < totalSteps && (
            <div
              className={cn(
                'w-12 h-0.5 mx-1 transition-all duration-300',
                isCompleted ? 'bg-elev8-blue/60' : 'bg-white/10'
              )}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

interface ServiceButtonProps {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const ServiceButton = ({ selected, onClick, children }: ServiceButtonProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-3 rounded-full border text-center transition-all duration-200 text-sm font-medium relative',
        selected
          ? 'border-elev8-blue bg-elev8-blue/10 text-elev8-blue'
          : 'border-white/20 hover:border-white/40 text-white/80 hover:text-white'
      )}
    >
      <div className="flex items-center justify-center gap-2">
        {selected && (
          <div className="absolute top-1 right-1 w-4 h-4 bg-elev8-blue rounded-full flex items-center justify-center">
            <Check size={10} className="text-white" />
          </div>
        )}
        <span>{children}</span>
      </div>
    </button>
  );
};

export default ContactForm;
